import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Loader2, PartyPopper, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import { useAuth, dashboardPathForRole } from "@/context/AuthContext";

const STEPS = ["Akun", "Data Diri", "Program", "Konfirmasi"];

export default function Register() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [searchParams] = useSearchParams();
  const [agentName, setAgentName] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", password: "", confirm_password: "",
    nik: "", dob: "", gender: "", address: "", city: "", province: "",
    program_type: "umrah", program_id: "", preferred_departure: "", referral_code: searchParams.get("ref") || "", agree: false,
  });
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/public/programs`).then(({ data }) => setPrograms(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.referral_code) { setAgentName(null); return; }
    axios.get(`${API}/public/referral/${form.referral_code}`).then(({ data }) => setAgentName(data.valid ? data.agent_name : null)).catch(() => setAgentName(null));
  }, [form.referral_code]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.name || !form.phone || !form.email || !form.password) return "Mohon lengkapi semua data akun";
      if (form.password.length < 6) return "Password minimal 6 karakter";
      if (form.password !== form.confirm_password) return "Konfirmasi password tidak sesuai";
    }
    if (step === 1) {
      if (!form.nik || form.nik.length !== 16) return "NIK harus 16 digit";
      if (!form.dob || !form.gender || !form.address || !form.city || !form.province) return "Mohon lengkapi data diri";
    }
    if (step === 2) {
      if (!form.program_type) return "Pilih jenis program";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!form.agree) return toast.error("Anda harus menyetujui penggunaan data");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, form);
      setUser(data);
      setSuccess(true);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F9F7] px-6 py-16">
        <div className="w-full max-w-md rounded-3xl bg-white p-10 text-center shadow-[0_8px_40px_rgba(15,107,79,0.08)]" data-testid="register-success-card">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#0F6B4F]/10 text-[#0F6B4F]">
            <PartyPopper className="h-8 w-8" />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-bold text-[#1F2937]">Pendaftaran Berhasil!</h1>
          <p className="mt-2 text-sm text-[#64748B]">
            Pendaftaran berhasil. Silakan masuk ke dashboard untuk melengkapi data Anda.
          </p>
          <Button
            data-testid="register-success-dashboard-button"
            onClick={() => navigate(dashboardPathForRole("jamaah"))}
            className="mt-6 h-11 w-full rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white hover:opacity-90"
          >
            Ke Dashboard Saya
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F9F7] px-4 py-12 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0F6B4F] to-[#1677B8] text-white font-heading font-bold">K</div>
          <span className="font-heading text-lg font-bold text-[#1F2937]">KBIHU Muhammadiyah Surabaya</span>
        </Link>

        <div className="rounded-3xl bg-white p-6 shadow-[0_8px_40px_rgba(15,107,79,0.08)] sm:p-10" data-testid="register-card">
          <div className="mb-8 flex items-center justify-between">
            {STEPS.map((label, idx) => (
              <div key={label} className="flex flex-1 items-center">
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    data-testid={`register-step-indicator-${idx}`}
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                      idx < step ? "bg-[#0F6B4F] text-white" : idx === step ? "bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white" : "bg-slate-100 text-[#64748B]"
                    }`}
                  >
                    {idx < step ? <Check className="h-4 w-4" /> : idx + 1}
                  </div>
                  <span className="hidden text-xs text-[#64748B] sm:block">{label}</span>
                </div>
                {idx < STEPS.length - 1 && <div className={`mx-2 h-0.5 flex-1 ${idx < step ? "bg-[#0F6B4F]" : "bg-slate-100"}`} />}
              </div>
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-4" data-testid="register-step-account">
              <h2 className="font-heading text-xl font-bold text-[#1F2937]">Buat Akun</h2>
              <div>
                <Label>Nama Lengkap</Label>
                <Input data-testid="register-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Nomor HP</Label>
                  <Input data-testid="register-phone-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="register-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Password</Label>
                  <Input data-testid="register-password-input" type="password" value={form.password} onChange={(e) => update("password", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label>Konfirmasi Password</Label>
                  <Input data-testid="register-confirm-password-input" type="password" value={form.confirm_password} onChange={(e) => update("confirm_password", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="register-step-personal">
              <h2 className="font-heading text-xl font-bold text-[#1F2937]">Data Diri</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>NIK</Label>
                  <Input data-testid="register-nik-input" maxLength={16} value={form.nik} onChange={(e) => update("nik", e.target.value.replace(/\D/g, ""))} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label>Tanggal Lahir</Label>
                  <Input data-testid="register-dob-input" type="date" value={form.dob} onChange={(e) => update("dob", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
              </div>
              <div>
                <Label>Jenis Kelamin</Label>
                <RadioGroup value={form.gender} onValueChange={(v) => update("gender", v)} className="mt-2 flex gap-6">
                  <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="Laki-laki" data-testid="register-gender-male" /> Laki-laki</label>
                  <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="Perempuan" data-testid="register-gender-female" /> Perempuan</label>
                </RadioGroup>
              </div>
              <div>
                <Label>Alamat</Label>
                <Input data-testid="register-address-input" value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Kota/Kabupaten</Label>
                  <Input data-testid="register-city-input" value={form.city} onChange={(e) => update("city", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label>Provinsi</Label>
                  <Input data-testid="register-province-input" value={form.province} onChange={(e) => update("province", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="register-step-program">
              <h2 className="font-heading text-xl font-bold text-[#1F2937]">Pilih Program</h2>
              <div>
                <Label>Haji / Umrah</Label>
                <RadioGroup value={form.program_type} onValueChange={(v) => update("program_type", v)} className="mt-2 flex gap-6">
                  <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="haji" data-testid="register-type-haji" /> Haji</label>
                  <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="umrah" data-testid="register-type-umrah" /> Umrah</label>
                </RadioGroup>
              </div>
              <div>
                <Label>Program</Label>
                <Select value={form.program_id} onValueChange={(v) => update("program_id", v)}>
                  <SelectTrigger data-testid="register-program-select" className="mt-1.5 h-11 rounded-xl">
                    <SelectValue placeholder="Pilih program (opsional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.filter((p) => p.type === form.program_type).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferensi Tanggal Keberangkatan</Label>
                <Input data-testid="register-preferred-departure-input" type="date" value={form.preferred_departure} onChange={(e) => update("preferred_departure", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <div>
                <Label>Kode Referral Agen (Opsional)</Label>
                <Input data-testid="register-referral-code-input" value={form.referral_code} onChange={(e) => update("referral_code", e.target.value.toUpperCase())} placeholder="Contoh: FITRI001" className="mt-1.5 h-11 rounded-xl uppercase" />
                {agentName && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#92720C]" data-testid="register-referral-agent-name">
                    <Handshake className="h-3.5 w-3.5" /> Direferensikan oleh agen: {agentName}
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="register-step-confirmation">
              <h2 className="font-heading text-xl font-bold text-[#1F2937]">Konfirmasi Pendaftaran</h2>
              <dl className="grid grid-cols-1 gap-3 rounded-xl bg-[#F5F9F7] p-5 text-sm sm:grid-cols-2">
                <div><dt className="text-[#64748B]">Nama</dt><dd className="font-medium text-[#1F2937]">{form.name}</dd></div>
                <div><dt className="text-[#64748B]">Email</dt><dd className="font-medium text-[#1F2937]">{form.email}</dd></div>
                <div><dt className="text-[#64748B]">Nomor HP</dt><dd className="font-medium text-[#1F2937]">{form.phone}</dd></div>
                <div><dt className="text-[#64748B]">NIK</dt><dd className="font-medium text-[#1F2937]">{form.nik}</dd></div>
                <div><dt className="text-[#64748B]">Kota</dt><dd className="font-medium text-[#1F2937]">{form.city}, {form.province}</dd></div>
                <div><dt className="text-[#64748B]">Program</dt><dd className="font-medium capitalize text-[#1F2937]">{form.program_type}</dd></div>
              </dl>
              <label className="flex items-start gap-3 text-sm text-[#1F2937]">
                <Checkbox checked={form.agree} onCheckedChange={(v) => update("agree", v)} data-testid="register-agree-checkbox" className="mt-0.5" />
                Saya menyetujui penggunaan data untuk proses pendaftaran dan pelayanan KBIHU Muhammadiyah Surabaya.
              </label>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            <Button variant="outline" disabled={step === 0} onClick={back} data-testid="register-back-button" className="gap-2 rounded-full">
              <ChevronLeft className="h-4 w-4" /> Kembali
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} data-testid="register-next-button" className="gap-2 rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white hover:opacity-90">
                Lanjut <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading || !form.agree} data-testid="register-submit-button" className="gap-2 rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white hover:opacity-90">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Pendaftaran"}
              </Button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-[#64748B]">
          Sudah memiliki akun?{" "}
          <Link to="/login" data-testid="register-login-link" className="font-medium text-[#0F6B4F] hover:underline">Masuk di sini</Link>
        </p>
      </div>
    </div>
  );
}
